import {Calendar, Drawer, Modal, Select} from 'antd'
import {timetable_getsByClassesOwner_Api} from 'apis'
import {getId} from 'apis/model/base'
import classNames from 'classnames'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import dayjs from 'dayjs'
import {getLocale} from 'helpers/getLocale'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import {callApi} from 'modules/asyncCache/callApi'
import {LocalContext} from 'modules/local/LocalContext'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect, useMemo, useState} from 'react'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import LoadingBlock from 'views/Loading/LoadingBlock'
import {getResponseItems, renderIf} from 'views/Shared'
import CouseClassTimetableFilter from '../components/filters/CouseClassTimetableFilter'
import {dropdownComponents} from "../../../AppLocals/custom/ReactSelectComponents";

// Backend giới hạn cứng 10 record/trang (Common::$itemPerPage) và không nhận
// per_page tùy chỉnh — lịch cần TOÀN BỘ phân công (để tự lọc theo từng ngày ở
// client) nên phải tự merge nhiều trang thay vì chỉ lấy trang 1 (khác với
// usePaginationHelper vốn cho user bấm "load more" từng trang).
const MAX_PAGES = 30

const fetchTimetablePage = (page, {apiInfo, query, values}) =>
    callApi({
        apiInfo: {...apiInfo, paramsFields: [...apiInfo.paramsFields, 'page']},
        query,
        values: {...values, page},
    })

// Gọi trang 1 trước để biết tổng số trang, rồi gọi SONG SONG hết các trang
// còn lại (thay vì tuần tự từng trang một) — mỗi trang là 1 request độc lập,
// không phụ thuộc nhau, nên không có lý do gì phải chờ lần lượt.
const fetchAllTimetablePages = async (params) => {
    const firstResponse = await fetchTimetablePage(1, params)
    let items = getResponseItems(firstResponse)

    const total = _.get(firstResponse, 'data.meta.pagination.total', 0)
    const per_page = _.get(firstResponse, 'data.meta.pagination.per_page', 0)
    const total_pages = per_page ? Math.min(Math.ceil(total / per_page), MAX_PAGES) : 1

    if (total_pages > 1) {
        const restPages = Array.from({length: total_pages - 1}, (_, i) => i + 2)
        const restResponses = await Promise.all(
            restPages.map((page) => fetchTimetablePage(page, params))
        )
        restResponses.forEach((response) => {
            items = items.concat(getResponseItems(response))
        })
    }

    return items
}

const buildLessonsByDate = (items = []) => {
    const map = {}
    items.forEach((item) => {
        const {
            course_class,
            teacher,
            subject,
            location_address,
            is_holiday,
            timetable_details = [],
        } = item ?? {}
        timetable_details.forEach((detail) => {
            const {start_time} = detail ?? {}
            if (!start_time) return
            const dateKey = dayjs(start_time).format('YYYYMMDD')
            map[dateKey] = map[dateKey] || []
            map[dateKey].push({
                ...detail,
                course_class,
                teacher,
                subject,
                location_address,
                is_holiday,
            })
        })
    })
    // "Ngày nghỉ" là 1 phase (timetable) riêng, không tự huỷ buổi học định kỳ
    // của phase khác cùng lớp cùng ngày ở tầng dữ liệu — nên ưu tiên hiển thị
    // ở đây: cùng 1 lớp, cùng 1 ngày, nếu có bản ghi is_holiday=1 thì ẩn các
    // buổi học thường trùng ngày của lớp đó, chỉ giữ lại bản ghi nghỉ.
    Object.keys(map).forEach((dateKey) => {
        const byClass = _.groupBy(map[dateKey], (lesson) =>
            getId(lesson.course_class)
        )
        map[dateKey] = Object.values(byClass).flatMap((group) => {
            const holidayLessons = group.filter((lesson) => lesson.is_holiday === 1)
            return holidayLessons.length ? holidayLessons : group
        })
    })
    return map
}

const isPastLesson = (lesson) => dayjs(lesson.end_time).isBefore(dayjs())

// Ô ngày chỉ hiện SỐ LƯỢNG + trạng thái tổng, không liệt kê từng lớp trực tiếp
// trong ô — vì 1 ngày có thể có hàng chục lớp, chip từng buổi sẽ tràn/vô nghĩa
// ở mật độ cao. Chi tiết từng buổi xem trong popup khi bấm vào ngày.
const DayCell = ({lessons = [], onSelect}) => {
    const t = useTranslate()
    if (!lessons.length) {
        return <div className="h-full bg-blue-50 rounded-t-lg"/>
    }
    const allPast = lessons.every(isPastLesson)

    return (
        <div
            onClick={(event) => {
                event.stopPropagation()
                onSelect()
            }}
            className={classNames(
                'h-full flex flex-col justify-center gap-0.5 px-1.5 py-1 rounded-t-lg cursor-pointer border',
                allPast
                    ? 'bg-gray-50 border-gray-100'
                    : 'bg-blue-100 border-blue-100 hover:border-blue-400'
            )}>
            <div className="flex items-center gap-1">
                <span
                    className={classNames(
                        'text-2xs font-bold truncate',
                        allPast ? 'text-color-400' : 'text-color-100'
                    )}>
                  {lessons.length} {t('classes').toLowerCase()}
                </span>
            </div>
        </div>
    )
}

const LessonItem = ({lesson}) => {
    const t = useTranslate()
    const {
        course_class,
        teacher,
        subject,
        location_address,
        start_time,
        end_time,
        is_holiday,
    } = lesson ?? {}
    return (
        <div className="p-3 flex flex-col gap-1 border border-color-50 rounded-lg">
            <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-sm text-blue-600">
          {dayjs(start_time).format('HH:mm')} -{' '}
            {dayjs(end_time).format('HH:mm')}
        </span>
                {renderIf(
                    is_holiday === 1,
                    <span className="font-semibold text-xs text-primary-500 italic">
            {t('holiday')}
          </span>
                )}
            </div>
            <span className="font-semibold text-sm text-color-000">
        {getTitle(course_class)}
      </span>
            <div className="flex flex-wrap gap-x-3 text-xs text-color-300 italic">
                {renderIf(
                    teacher,
                    <span>{`${t('teacher')}: ${getTitle(teacher)}`}</span>
                )}
                {renderIf(
                    subject,
                    <span>{`${t('subjects')}: ${getTitle(subject)}`}</span>
                )}
                {renderIf(location_address, <span>{location_address}</span>)}
            </div>
        </div>
    )
}

const DayDetailContent = ({lessons = []}) => {
    // Sắp theo giờ bắt đầu — 1 ngày có thể có rất nhiều buổi học của nhiều lớp
    // khác nhau (lịch tổng org-wide), không sắp xếp thì thứ tự lộn xộn theo
    // đúng thứ tự dữ liệu trả về, không theo giờ trong ngày.
    const sortedLessons = _.sortBy(lessons, (lesson) => dayjs(lesson.start_time).valueOf())
    return (
        <div
            style={{maxHeight: '70vh'}}
            className="flex flex-col gap-2 overflow-y-auto pr-1">
            {sortedLessons.map((lesson, index) => (
                <LessonItem
                    key={index}
                    lesson={lesson}
                />
            ))}
        </div>
    )
}

const NAV_YEAR_RANGE = 5

// Năm/Tháng/chế độ xem — thay cho header mặc định của Antd Calendar (nằm tách
// biệt bên trong Calendar) để đặt chung hàng với Trạng thái trong toolbar.
const CalendarNavControls = ({value, mode, onChange}) => {
    const t = useTranslate()
    const currentYear = dayjs().year()
    const yearOptions = useMemo(() => {
        const years = []
        for (let y = currentYear - NAV_YEAR_RANGE; y <= currentYear + NAV_YEAR_RANGE; y += 1) {
            years.push({value: y, label: String(y)})
        }
        return years
    }, [currentYear])
    const monthOptions = useMemo(
        () =>
            Array.from({length: 12}, (_, i) => ({
                value: i,
                label: `Th${String(i + 1).padStart(2, '0')}`,
            })),
        []
    )

    return (
        <div className="flex items-center gap-2 flex-shrink-0">
            <Select
                suffixIcon={<dropdownComponents.DropdownIndicator/>}
                value={value.year()}
                options={yearOptions}
                onChange={(year) => onChange(value.clone().year(year), mode)}
            />
            {mode === 'month' && (
                <Select
                    suffixIcon={<dropdownComponents.DropdownIndicator/>}
                    value={value.month()}
                    options={monthOptions}
                    onChange={(month) => onChange(value.clone().month(month), mode)}
                />
            )}
            <div className="flex rounded-md border border-color-200 overflow-hidden">
                {['month', 'year'].map((m) => (
                    <button
                        key={m}
                        onClick={() => onChange(value, m)}
                        className={classNames(
                            'px-2.5 py-1 flex flex-center font-medium transition-colors',
                            mode === m
                                ? 'bg-indigo-600 text-white'
                                : 'background text-color-300 hover:text-color-100'
                        )}>
                        {m === 'month' ? t('month') : t('year')}
                    </button>
                ))}
            </div>
        </div>
    )
}

// lockedCourseId/hideHeader: cho phép nhúng NGUYÊN khối lịch này vào màn khác đã tự có sẵn ngữ
// cảnh 1 khóa cụ thể (CourseClassOperations.js - "Điều hành Khóa & Lớp học", sau khi đã chọn khóa
// ở bước 1 thì không cần chọn lại course_id ở đây nữa) - khác trang "Thời khóa biểu" độc lập (không
// truyền 2 prop này) vẫn giữ nguyên hành vi cũ (tự chọn khóa, có tiêu đề riêng).
const TimetableManager = ({owner_id, owner_type, lockedCourseId, hideHeader = false}) => {
    const t = useTranslate()
    const {lang} = useContext(LocalContext)
    const {isSm} = useContext(LayoutContext)

    const [filter, setFilter] = useState({
        course_id: lockedCourseId,
    })
    const [selectedDate, setSelectedDate] = useState(null)
    const [calendarValue, setCalendarValue] = useState(() => dayjs())
    const [calendarMode, setCalendarMode] = useState('month')

    useEffect(() => {
        if (lockedCourseId) setFilter((prev) => ({...prev, course_id: lockedCourseId}))
    }, [lockedCourseId])

    const [items, setItems] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    // Không lọc from_time_open/to_time_open theo tháng đang xem: backend so field
    // này với "timetables.from_date" (ngày BẮT ĐẦU phân công), không phải ngày
    // diễn ra buổi học — lớp bắt đầu dạy từ tháng trước vẫn phải hiện lịch tháng
    // này. Không lọc status (còn hiệu lực/hết hiệu lực) nữa vì nó so với thời
    // điểm hiện tại, không phải tháng đang xem — bấm lùi lịch về quá khứ sẽ bị
    // ẩn nhầm dữ liệu; việc khớp đúng ngày làm ở client theo
    // timetable_details[].start_time (buildLessonsByDate).
    useEffect(() => {
        if (!owner_id || !owner_type) return undefined
        let cancelled = false
        setIsLoading(true)
        fetchAllTimetablePages({
            apiInfo: timetable_getsByClassesOwner_Api,
            query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
            values: {...filter},
        }).then((allItems) => {
            if (!cancelled) {
                setItems(allItems)
                setIsLoading(false)
            }
        })
        return () => {
            cancelled = true
        }
    }, [
        owner_id,
        owner_type,
        filter.course_id,
    ])

    const lessonsByDate = useMemo(() => buildLessonsByDate(items), [items])
    const selectedLessons = selectedDate
        ? lessonsByDate[selectedDate.format('YYYYMMDD')] || []
        : []

    return (
        <div className="flex flex-1 flex-col gap-4">
            {!hideHeader && (
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex flex-col flex-1">
                        <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                            {t('timetables')}
                        </h2>
                        <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                        <div className="mt-1 mr-4 text-sm text-color-300 tracking-wider">
                            {t('This section displays the entire timetable filtered by class and subject for administrators to track the daily teaching schedule.')}
                        </div>
                    </div>
                </div>
            )}
            <div className="p-3 flex flex-wrap items-end justify-between gap-3 border border-color-50 background shadow-sm rounded-xl">
                {!lockedCourseId && (
                    <CouseClassTimetableFilter
                        owner_id={owner_id}
                        owner_type={owner_type}
                        value={filter}
                        onChange={setFilter}
                    />
                )}
                <CalendarNavControls
                    value={calendarValue}
                    mode={calendarMode}
                    onChange={(value, mode) => {
                        setCalendarValue(value)
                        setCalendarMode(mode)
                    }}
                />
            </div>
            {/* LoadingBlock render NGAY TRONG card lịch (thay chỗ, không chèn thêm
          khối riêng phía trên) — tránh đẩy legend/lịch nhảy vị trí mỗi lần tải
          lại dữ liệu (đổi Khóa đào tạo, đổi tháng...). */}
            <div className="border border-color-100 background shadow-sm rounded-xl overflow-hidden">
                {isLoading ? (
                    <LoadingBlock/>
                ) : (
                    <>
                        <div className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-color-50 font-medium text-color-200 background-100">
                            <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded-sm bg-blue-200 border border-blue-200"/>
                                {t('regular class session')}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded-sm bg-gray-200 border border-gray-300"/>
                                {t('done')}
                            </span>
                        </div>
                        <Calendar
                            locale={getLocale(lang)}
                            className="CustomCalendar background"
                            value={calendarValue}
                            mode={calendarMode}
                            headerRender={() => null}
                            onPanelChange={(date, mode) => {
                                setCalendarValue(date)
                                setCalendarMode(mode)
                            }}
                            onSelect={(date, {source} = {}) => {
                                // Header mặc định của Antd đã tắt (headerRender=null) nên onSelect giờ
                                // chỉ còn bắn khi bấm thẳng vào ô ngày.
                                if (source === 'date') setSelectedDate(date)
                            }}
                            cellRender={(value) => (
                                <DayCell
                                    lessons={lessonsByDate[value.format('YYYYMMDD')] || []}
                                    onSelect={() => setSelectedDate(value)}
                                />
                            )}
                        />
                    </>
                )}
            </div>
            {selectedDate &&
            (isSm ? (
                <Drawer
                    title={
                        <div className="font-semibold text-sm text-color-000 leading-snug">
                            {`${_.capitalize(
                                selectedDate.format('dddd')
                            )}, ${selectedDate.format('DD/MM/YYYY')}`}
                        </div>
                    }
                    height="fit-content"
                    styles={{body: {maxHeight: '80vh', padding: 16}}}
                    placement="bottom"
                    closable={false}
                    open={!!selectedDate}
                    onClose={() => setSelectedDate(null)}
                    footer={
                        <div
                            onClick={() => setSelectedDate(null)}
                            className="text-center font-medium text-sm text-color-200">
                            {t('close')}
                        </div>
                    }>
                    <DayDetailContent lessons={selectedLessons}/>
                </Drawer>
            ) : (
                <Modal
                    width={520}
                    footer={null}
                    destroyOnHidden={true}
                    open={!!selectedDate}
                    className="custom-modal"
                    onCancel={() => setSelectedDate(null)}
                    title={
                        <div className="text-center font-bold text-sm text-color-000 uppercase">
                            {`${_.capitalize(
                                selectedDate.format('dddd')
                            )}, ${selectedDate.format('DD/MM/YYYY')}`}
                        </div>
                    }>
                    <DayDetailContent lessons={selectedLessons}/>
                </Modal>
            ))}
            <div className="h-16"/>
        </div>
    )
}

const CourseClassTimetable = compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(TimetableManager)

export default CourseClassTimetable
